// eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-imports-ts
import { h } from '@stencil/core';
import { Component, Host, Element, Prop, State, Watch, Fragment, Env } from '@stencil/core';

import { DeviceType } from '../../../../types/base/affiliate/characteristics';
import { AffiliateControllerResponse } from '../../../../types/controllers/affiliate';
import { FormattedPromotion } from '../../types';

import { formatPromotions } from '../../utils';
import { ageClass } from '../../utils/age-check';
import { getPlacement } from '../../utils/api';
import { filterPromotions } from '../../utils/filter';
import { getPromotionTableTitle } from '../../utils/location';

import state, { setLocationToStore, stencilStore } from '../../utils/store';
import LocationDropdown from '../functional/LocationDropdown';

import PromotionCard from '../functional/PromotionCard';

import type { PromotionOptions, ComponentType } from '../../utils/analytics';

@Component({
    tag: 'bam-inline-promotion-block',
    styleUrl: 'bam-inline-promotion-block.css',
    shadow: true,
})
export class BamInlinePromotionBlock {
    private defaultCallback() {
        return null;
    }

    @Element() el: HTMLElement;

    // props
    /** (required) */
    @Prop() placementId: number;
    /** (required) */
    @Prop() propertyId: number;
    @Prop({ mutable: true }) location: string;
    /** (required) */
    @Prop() context: string;
    /** You cannot pass this parameter via an attribute. You must set the callback function after the DOM has loaded. */
    @Prop() callback: () => void = this.defaultCallback;

    // optional characteristics props
    @Prop() pageSlug: string;
    @Prop() subscriptionTier: 0 | 1 | 2 | 3;
    @Prop() wasPro: boolean;
    @Prop() subdivision: number;
    @Prop() league: string;
    @Prop() gameId: number;
    @Prop() ctaType: string;
    @Prop() isMobile: boolean;
    @Prop() deviceType: DeviceType;
    @Prop() isBetsyncUser: boolean;
    @Prop() userParentBookIds: string;
    @Prop() isLoggedIn: boolean;
    @Prop() sponsorSlug: string;

    // optional props for functionality
    /** This will determine the maximum number of promotions to display. */
    @Prop() maxLength: number;
    /** Overrides the start index calculation */
    @Prop() page: number;
    @Prop() moduleTitle: string;
    @Prop() defer: boolean;
    @Prop() devMode = false;
    @Prop() affiliate: string;
    @Prop() affiliateType: string;
    @Prop() internalId: string;
    @Prop() restart: false;
    @Prop() age: number;
    @Prop() hideUnderage = false;
    @Prop() darkMode = false;
    @Prop() terms: 'geo' | 'affiliate' = 'geo';
    /** Setting this to false will hide the surrounding border, title, and location dropdown */
    @Prop() showContainer = true;

    // state
    @State() promotions: Array<FormattedPromotion> = [];
    @State() toggle = false;
    @State() inlinePromotionBlocks = [];
    @State() loading = true;
    @State() locationToUse: string | null = null;

    @Watch('locationToUse')
    @Watch('subscriptionTier')
    onLocationChange() {
        this.promotions = [];
        getPlacement(
            {
                placement_id: this.placementId,
                property_id: this.propertyId,
                location: this.locationToUse,
                page_slug: this.pageSlug,
                subscription_tier: this.subscriptionTier,
                was_pro: this.wasPro,
                subdivision_id: this.subdivision,
                league: this.league,
                game_id: this.gameId,
                cta_type: this.ctaType,
                is_mobile: this.isMobile,
                device_type: this.deviceType,
                isBetsyncUser: this.isBetsyncUser,
                user_parent_book_ids: this.userParentBookIds ? this.userParentBookIds.split(',').map(Number) : [],
                is_logged_in: this.isLoggedIn,
                sponsor_slug: this.sponsorSlug,
                context: this.context,
            },
            this.formatAndSetPromotionsToState.bind(this),
            this.devMode,
            'Inline Promotion Block'
        );
    }

    @Watch('location')
    onLocationPropChange() {
        this.locationToUse = this.location || state.location;
    }

    connectedCallback() {
        this.locationToUse = this.location || state.location;
        this.inlinePromotionBlocks = [...document.querySelectorAll('bam-inline-promotion-block')];

        if (!this.defer && Env.BAM_HYDRATE !== 'true') {
            getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.locationToUse,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    user_parent_book_ids: this.userParentBookIds ? this.userParentBookIds.split(',').map(Number) : [],
                    is_logged_in: this.isLoggedIn,
                    sponsor_slug: this.sponsorSlug,
                    context: this.context,
                },
                this.formatAndSetPromotionsToState.bind(this),
                this.devMode,
                'Inline Promotion Block'
            );
        }

        // if location is not provided, subscribe to location in store
        stencilStore.onChange('location', newLocation => {
            this.locationToUse = newLocation;
        });
    }

    async componentWillLoad() {
        if (Env.BAM_HYDRATE === 'true') {
            await getPlacement(
                {
                    placement_id: this.placementId,
                    property_id: this.propertyId,
                    location: this.locationToUse,
                    page_slug: this.pageSlug,
                    subscription_tier: this.subscriptionTier,
                    was_pro: this.wasPro,
                    subdivision_id: this.subdivision,
                    league: this.league,
                    game_id: this.gameId,
                    cta_type: this.ctaType,
                    is_mobile: this.isMobile,
                    device_type: this.deviceType,
                    isBetsyncUser: this.isBetsyncUser,
                    user_parent_book_ids: this.userParentBookIds ? this.userParentBookIds.split(',').map(Number) : [],
                    is_logged_in: this.isLoggedIn,
                    sponsor_slug: this.sponsorSlug,
                    context: this.context,
                },
                this.formatAndSetPromotionsToState.bind(this),
                this.devMode,
                'Inline Promotion Block'
            );
        }
    }

    handleDropdown(evt: Event) {
        if (evt.target instanceof HTMLSelectElement) {
            const newLocation = evt.target.value;
            setLocationToStore(newLocation);
        }
    }

    handleToggle() {
        this.toggle = !this.toggle;
    }

    formatAndSetPromotionsToState(placement: AffiliateControllerResponse, location: string) {
        const adBlockIndex = this.inlinePromotionBlocks.findIndex(block => block === this.el);

        let prevLength = 0;
        let startIndex = this.inlinePromotionBlocks.slice(0, adBlockIndex).reduce((acc, curr) => {
            const isSamePlacement = Number(curr.getAttribute('placement-id')) === this.placementId;
            const isSameLocation = curr.getAttribute('location') === this.location;

            if (isSamePlacement && isSameLocation) {
                let retVal = acc + Number(curr.getAttribute('max-length') || 2);
                if (curr.getAttribute('restart') === 'true') {
                    // Rewind to get the correct order after the restart component
                    retVal = acc - prevLength;
                }
                prevLength = Number(curr.getAttribute('max-length') || 2);

                // if index is going to be greater than the length, we want to reset it to 0
                return retVal >= placement.promotions.length ? 0 : retVal;
            }
            return acc;
        }, 0);

        const maxLength = Number(this.maxLength || 2);
        if (this.page > 0) {
            const initialStart = (this.page - 1) * maxLength;
            startIndex =
                initialStart > placement.promotions.length ? initialStart % placement.promotions.length : initialStart;
        }

        const stopIndex = startIndex + maxLength;

        const getBlockPromotions = () => {
            const promolist = formatPromotions(placement.promotions, {
                allowAltLink: placement.allow_alternate_link,
                promotionListId: placement.id,
                location: location,
                context: this.context,
                propertyId: this.propertyId,
            });
            let promos = filterPromotions(
                {
                    internal_identifier: this.internalId,
                    affiliate_name: this.affiliate,
                    affiliate_type: this.affiliateType,
                },
                promolist
            );
            const hasFilters = this.internalId || this.affiliate || this.affiliateType;

            // prevent loop from running if placement doesn't have promos
            if (!promos.length) {
                return promos;
            }

            while (promos.length < stopIndex && !hasFilters) {
                promos = [...promos, ...promolist];
            }

            if (this.restart) {
                return promos.slice(0, this.maxLength || 2);
            }

            // first slice handles designated length
            // second slice prevents repeating promotions in same block
            return promos.slice(startIndex, stopIndex).slice(0, placement.promotions.length);
        };

        const promotions = getBlockPromotions();

        if (!promotions.length) {
            // eslint-disable-next-line no-console
            console.warn('BAM Promotion List: No active or valid promotions returned');
            this.promotions = [];
        } else {
            this.promotions = promotions;
        }

        this.locationToUse = !this.location ? location : this.locationToUse;
        this.loading = false;
    }

    render() {
        return (
            <Host class={ageClass(this.age, this.promotions, this.hideUnderage)}>
                <div class={`${this.darkMode ? 'dark-mode' : ''}`}>
                    <div class={this.showContainer ? 'side-rail font' : 'font'}>
                        {this.showContainer && (
                            <div class="header">
                                <h2 class="title title-font">
                                    {getPromotionTableTitle(this.locationToUse, this.moduleTitle)}
                                </h2>
                                <LocationDropdown
                                    location={this.locationToUse}
                                    handleDropdown={this.handleDropdown.bind(this)}
                                />
                            </div>
                        )}
                        <ul id="promo-container" class="list-container">
                            {!this.defer && !this.loading ? (
                                <Fragment>
                                    {this.promotions.length ? (
                                        <Fragment>
                                            {this.promotions
                                                .slice(0, this.toggle ? this.promotions.length : this.maxLength)
                                                .map(promotion => {
                                                    const eventOptions: {
                                                        componentType: ComponentType;
                                                        options: PromotionOptions;
                                                    } = {
                                                        componentType: 'Inline Promotion Block',
                                                        options: {
                                                            stateCode: this.locationToUse?.toUpperCase(),
                                                            context: this.context,
                                                            propertyId: Number(this.propertyId),
                                                            placementId: Number(this.placementId),
                                                            league: this.league,
                                                            allPromotions: this.promotions,
                                                            pageSlug: this.pageSlug,
                                                        },
                                                    };

                                                    return (
                                                        <promotion-card
                                                            key={promotion.id}
                                                            promotion={promotion}
                                                            use-box-shadow={true}
                                                            propertyId={this.propertyId}
                                                            eventOptions={eventOptions}
                                                            dark-mode={this.darkMode}
                                                            terms={this.terms}
                                                            callback={this.callback}
                                                        ></promotion-card>
                                                    );
                                                })}
                                        </Fragment>
                                    ) : (
                                        <p class="empty-state">
                                            No promotions available. Try selecting a different location.
                                        </p>
                                    )}
                                </Fragment>
                            ) : (
                                <PromotionCard
                                    promotion={null}
                                    logPromotionClick={() => {}}
                                    handleBonusCodeClick={() => {}}
                                    bonusCodeWasClicked={false}
                                    terms={this.terms}
                                ></PromotionCard>
                            )}
                        </ul>

                        {this.promotions.length > this.maxLength ? (
                            <div class="footer">
                                <button class="see-all" onClick={this.handleToggle.bind(this)}>
                                    {this.toggle ? 'Hide' : 'See all'}
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </Host>
        );
    }
}
